// Service hook (TS-2.5.01, Resource Accessor layer) - the only place in this feature that calls
// changesApi. Fetches once on mount and again on any push-channel invalidation, which is the same
// rule useRoadmap follows and for the same reason (decisions.md D-217): a write elsewhere
// broadcasts, and the handler's job is to re-enter the ordinary read chain rather than to apply a
// payload nobody sent. No poll timer - the card that shows what changed would otherwise be the one
// surface in the app that asked the server every few seconds whether anything had.
//
// A FAILURE HERE IS NOT A PAGE FAILURE. The roadmap is the board and the detail sections; the
// recent-changes card is context beside them. So this hook reports its own error and the card
// renders a quiet line, rather than the page taking the whole-screen error treatment useRoadmap's
// error drives. A card that could black out a working board would be a decoration with a veto.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import { changesApi } from "../../../api/changes";
import type { ChangeFeed } from "../../../api/generated/contracts";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseRecentChangesResult {
  readonly feed: ChangeFeed | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

/**
 * Fetches GET /api/roadmap/changes (ChangesController / IAuditManager.GetChangeFeedAsync).
 * `days` and `count` are clamped by the boundary, not here - PageDefaults' standing rule is that
 * how much one request may cost is the server's decision, and a client that clamped too would be
 * a second copy of a limit that can then disagree with the first.
 */
export function useRecentChanges(days: number, count: number): UseRecentChangesResult {
  const [feed, setFeed] = useState<ChangeFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void changesApi
      .get({ days, count })
      .then((next) => {
        setFeed(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "Recent changes could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [days, count]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh);

  return { feed, loading, error };
}
